import { memo, useMemo } from 'react';

import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';

import { LeaderBoardIcon, LightTooltip } from 'components';

import { TooltipBody } from './components';
import { useStyles } from './styles';

interface DoubleColumnChartProps {
  data?: number[][];
  title: string;
  scale?: number;
  backgroundColors?: string[];
  labels?: (string | number)[];
  tooltipLabels?: string[];
  levelBordered?: boolean;
}

const defaultBackground = ['#D582F9', '#0075B0'];

function getLabelsFromScale(scale: number): string[] {
  return Array(scale)
    .fill(0)
    .map((_, index) => String(index + 1));
}

function DoubleColumnChartWithoutMemo({
  title,
  data,
  labels,
  tooltipLabels,
  scale = 5,
  levelBordered = false,
  backgroundColors = defaultBackground,
}: DoubleColumnChartProps): JSX.Element {
  const classes = useStyles();

  const memoLevelLabels = useMemo(() => {
    if (labels && labels.length === scale) {
      return labels;
    }

    return getLabelsFromScale(scale);
  }, [labels, scale]);

  const memoData = useMemo(() => {
    const internData = data ?? [];
    let [firstData, secondData] = internData;
    firstData = firstData?.filter((_, index) => index < scale);
    secondData = secondData?.filter((_, index) => index < scale);

    const fakeData = Array(scale)
      .fill(0)
      .map(() => 0);
    const newData = [
      firstData && firstData.length ? firstData : fakeData,
      secondData && secondData.length ? secondData : fakeData,
    ];

    return newData;
  }, [scale, data]);

  const memoHasData = useMemo(() => {
    if (!data) {
      return false;
    }

    const [firstData, secondData] = data;

    return !!firstData.length && !!secondData.length;
  }, [data]);

  return (
    <div
      className={clsx(classes.root, {
        [classes.noData]: !memoHasData,
      })}
    >
      {!!data && memoHasData && (
        <>
          <Typography className={classes.title}>{title}</Typography>
          <div
            className={classes.chartContainer}
            style={{ gridTemplateColumns: `repeat(${scale}, 1fr)` }}
          >
            <div className={classes.levelsContainer}>
              {getLabelsFromScale(5).map(level => (
                <div
                  key={level}
                  className={clsx(classes.level, {
                    bordered: levelBordered,
                  })}
                />
              ))}
            </div>

            {memoData[0].map((column, index) => (
              <LightTooltip
                key={uuid()}
                placement="top"
                PopperProps={{
                  popperOptions: {
                    modifiers: {
                      placement: 'bottom',
                      offset: {
                        enabled: true,
                        offset: `0, -162px`,
                      },
                    },
                  },
                }}
                title={
                  <TooltipBody
                    labels={tooltipLabels}
                    data={[data[0][index], data[1][index]]}
                    backgrounds={backgroundColors}
                  />
                }
              >
                <div key={uuid()} className={classes.barsContainer}>
                  <div className={classes.barContainer}>
                    <div
                      className={clsx(classes.bar, {
                        'no-radius': (column ?? 0) < 4,
                      })}
                      style={{
                        height: `${column ?? 0}%`,
                        backgroundColor: backgroundColors[0],
                      }}
                    />
                  </div>

                  <div className={classes.barContainer}>
                    <div
                      className={clsx(classes.bar, {
                        'no-radius': (memoData[1][index] ?? 0) < 4,
                      })}
                      style={{
                        height: `${memoData[1][index] ?? 0}%`,
                        backgroundColor: backgroundColors[1],
                      }}
                    />
                  </div>
                </div>
              </LightTooltip>
            ))}
          </div>
          <div
            className={classes.levelLabelsContainer}
            style={{
              gridTemplateColumns: `repeat(${memoLevelLabels.length}, 1fr)`,
            }}
          >
            {memoLevelLabels.map(label => (
              <div key={label} className={classes.levelLabelWrapper}>
                <Typography
                  className={classes.levelLabel}
                  color="textSecondary"
                >
                  {label}
                </Typography>
              </div>
            ))}
          </div>
        </>
      )}
      {!memoHasData && (
        <div className={classes.noDataIconWrapper}>
          <LeaderBoardIcon className={classes.noDataIcon} />
        </div>
      )}
    </div>
  );
}

export const DoubleColumnChart = memo(
  DoubleColumnChartWithoutMemo,
) as typeof DoubleColumnChartWithoutMemo;
